import TextInput from 'components/customComponents/Experiment/ImagingSchedule/TextInput';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Table } from './scoreTable';
import { EISelectBox } from 'components/customComponents/Layers/EICheckBox';
import {
  EntityStateDTO,
  ImageStoreDTO,
  ImageStoreLayoutDTO,
} from 'business/models/Models';
import { ModalDetails, SelectNameValue } from 'business/models/CustomModels';
import { getNewId } from 'utils/Global';
import WellService from 'business/services/WellService';
import cloneDeep from 'lodash/cloneDeep';
import ImageCompression from 'enums/ImageCompression';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import { RMModal } from 'components/modal';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { RMCheckBoxY } from 'components/customComponents/Controls/RMCheckbox';

interface Props {
  imageStores: ImageStoreDTO[];
  setImagesStores(imageStoreDTO: ImageStoreDTO[]): void;
  fetchImageScores(): void;
}
export const ImageStores = memo((props: Props) => {
  const { imageStores, setImagesStores, fetchImageScores } = props;
  const [values, setValues] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [codecList, setCodecList] = useState<SelectNameValue[]>([]);
  const [imageStoreLayouts, setImageStoreLayouts] = useState<ImageStoreLayoutDTO[]>([]);

  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    showButtons: true,
    onCancel: () => {},
  });

  useEffect(() => {
    setupTable();
    fetchCodec();
    fetchLayouts();
  }, [imageStores, selectedRowIndex]);

  const fetchCodec = useCallback(async () => {
    const globalPreferencesClient = new GlobalPreferencesService();
    const tempCodec = await globalPreferencesClient.getPropertiesByName(
      'ImageCompression',
    );
    const newCodecList = tempCodec.map((codec) => {
      const nameValue: SelectNameValue = { value: codec.id, name: codec.name };
      return nameValue;
    });
    setCodecList(cloneDeep([...newCodecList]));
  }, []);

  const fetchLayouts = useCallback(async () => {
    const wellservice = new WellService();
    const tempImageStoreLayouts = await wellservice.getImageStoreLayouts();
    setImageStoreLayouts(cloneDeep([...tempImageStoreLayouts]));
  }, []);

  const setupTempTable = useCallback(() => {
    if (values !== null) {
      return (
        <Table
          columns={getTableColumns}
          data={values}
          onRowClick={onSelectRow}
          selectedRowIndex={selectedRowIndex}
        />
      );
    }
    return null;
  }, [values]);

  const onchangeName = useCallback(
    (value, index) => {
      const tempStores = [...imageStores];
      const isDuplicateValue = tempStores.some(
        (item, itemIndex) => item.name === value && itemIndex !== index,
      );
      if (isDuplicateValue) {
        alert('Please provide a unique name.');
        return;
      }
      tempStores[index].name = value;
      tempStores[index].entityState = EntityStateDTO.Modified;
      setImagesStores(tempStores);
    },
    [imageStores],
  );

  const handleDefaultChange = useCallback(
    (index: number) => {
      const tempStores = imageStores.map(
        (store, i) =>
          new ImageStoreDTO({
            ...store,
            isDefault: i === index,
            entityState: i === index ? EntityStateDTO.Modified : store.entityState,
          }),
      );

      setImagesStores(tempStores);
    },
    [imageStores],
  );

  const onChangeBasePath = useCallback(
    async (value, prevValue, index) => {
      if (value === prevValue) {
        return;
      }
      const wellservice = new WellService();
      const isInUse = await wellservice.isImageStoreInUseByImageManagement(
        imageStores[index].id,
      );
      if (isInUse.value === true) {
        setModalDetails({
          showModal: true,
          onOk: () => setBasepath(value, index),
          setShowModal: () => {
            setModalDetails({ ...modalDetails, showModal: false });
          },
          body: (
            <div id='imagestores-changebasepath-body' style={{ width: '100%' }}>
              <label id='imagestores-changebasepath-label'>
                Are you sure you want to change the Base Path of this image store? There
                are currently images assigned to this image store. If you proceed ROCK
                MAKER may be unable to load images.
              </label>
            </div>
          ),
          heading: 'ROCK MAKER',
          isOkOnly: false,
          okButtonText: 'Yes, Proceed',
          showButtons: true,
          onCancel: () => {
            setBasepath(prevValue, index);
          },
        });
      } else {
        setBasepath(value, index);
      }
    },
    [imageStores],
  );
  const setBasepath = useCallback(
    (value, index) => {
      const tempStores = [...imageStores];

      if (tempStores[index].codec !== value) {
        tempStores[index].basePath = value;
        tempStores[index].entityState = EntityStateDTO.Modified;
      }
      setImagesStores(tempStores);
      setModalDetails({ ...modalDetails, showModal: false });
    },
    [imageStores],
  );
  const onChangeLayout = useCallback(
    (value, index) => {
      const tempStores = [...imageStores];
      tempStores[index].layout = value;
      tempStores[index].entityState = EntityStateDTO.Modified;
      setImagesStores(tempStores);
    },
    [imageStores],
  );
  const onChangeCodec = useCallback(
    async (value, index) => {
      const wellservice = new WellService();
      const isInUse = await wellservice.isImageStoreInUseByImageManagement(
        imageStores[index].id,
      );
      if (isInUse.value === true) {
        setModalDetails({
          showModal: true,
          onOk: () => setCodec(+value, index),
          setShowModal: () => {
            setModalDetails({ ...modalDetails, showModal: false });
          },
          body: (
            <div id='imagestores-changecodec-body' style={{ width: '100%' }}>
              <label id='imagestores-changecodec-label'>
                Are you sure you want to change the Image Codec of this image store? There
                are currently images assigned to this image store. If you proceed ROCK
                MAKER may be unable to load images.
              </label>
            </div>
          ),
          heading: 'ROCK MAKER',
          isOkOnly: false,
          okButtonText: 'Yes, Proceed',
          showButtons: true,
          onCancel: () => {
            setCodec(imageStores[index].codec, index);
          },
        });
      } else {
        setCodec(+value, index);
      }
    },
    [imageStores],
  );
  const setCodec = useCallback(
    (value, index) => {
      const tempStores = [...imageStores];
      if (tempStores[index].codec !== value) {
        tempStores[index].codec = value;
        tempStores[index].entityState = EntityStateDTO.Modified;
      }
      setImagesStores(tempStores);
      setModalDetails({ ...modalDetails, showModal: false });
    },
    [imageStores],
  );
  const addNewConfig = useCallback(() => {
    let diskSize = 0;
    let availableSize = 0;
    const basePath = 'C:\\RockMakerStorage';
    const imageStore = imageStores.filter((image) => image.basePath === basePath);
    if (imageStore.length > 0) {
      diskSize = imageStore[0].diskSize;
      availableSize = imageStore[0].availableSize;
    }
    const layouts = new ImageStoreLayoutDTO();
    {
      layouts.name = 'MultiCaptureLayout';
      layouts.id = 0;
      layouts.imageCodec = 0;
    }
    if (imageStoreLayouts.length > 0) {
      layouts.name = imageStoreLayouts[0].name;
      layouts.id = imageStoreLayouts[0].id;
      layouts.imageCodec = imageStoreLayouts[0].imageCodec;
    }
    const newItem = new ImageStoreDTO({
      id: getNewId(),
      name: '',
      codec: ImageCompression.DotNetJpeg411,
      basePath: basePath,
      imageStoreLayoutID: layouts.id,
      layout: layouts.name,
      diskSize: diskSize,
      availableSize: availableSize,
      entityState: EntityStateDTO.New,
      isDefault: false,
    });
    const tempStores = [...imageStores];
    const newList = tempStores.concat(newItem);
    setImagesStores(newList);
    setSelectedRowIndex(newList.length - 1);
  }, [imageStores, setImagesStores, setSelectedRowIndex]);

  const showModalMessage = (message: string) => {
    setModalDetails({
      showModal: true,
      onOk: () => {
        setModalDetails({ ...modalDetails, showModal: false });
      },
      setShowModal: () => {
        setModalDetails({ ...modalDetails, showModal: false });
      },
      body: (
        <div id='imagestores-modalmessage-body' style={{ width: '100%' }}>
          <label id='imagestores-modalmessage-label' >{message}</label>
        </div>
      ),
      heading: 'Error',
      isOkOnly: true,
      okButtonText: 'Ok',
      showButtons: true,
      onCancel: () => {},
    });
  };

  const deleteConfig = useCallback(async () => {
    if (selectedRowIndex === -1) {
      return;
    }

    const wellservice = new WellService();
    let isInUse = await wellservice.isImageStoreInUseByImageManagement(
      imageStores[selectedRowIndex].id,
    );
    if (isInUse.value === true) {
      isInUse = await wellservice.isImageStoreInUseByImage(
        imageStores[selectedRowIndex].id,
      );
      if (isInUse.value === true) {
        showModalMessage(
          'You cannot delete this image store because it is currently in use.',
        );
      }
    } else if (imageStores?.[selectedRowIndex]?.isDefault) {
      showModalMessage('You cannot delete a default image store.');
    } else {
      const tempStoresList = [...imageStores];
      const newfilteredList = tempStoresList.filter((item, index) => {
        if (index !== selectedRowIndex) {
          return item;
        }
      });
      setImagesStores(newfilteredList);
      setSelectedRowIndex(-1);
    }
  }, [
    imageStores,
    selectedRowIndex,
    WellService,
    setImagesStores,
    setSelectedRowIndex,
    modalDetails,
    setModalDetails,
  ]);
  const onSelectRow = useCallback(
    (row: { index: number }): void => {
      setSelectedRowIndex(row.index);
    },
    [imageStores],
  );
  const getTableColumns: object[] = [
    {
      Header: <span id="imagestore-header-selector"></span>,
      accessor: 'selector',
      className: 'imagestore-selection',
    },
    {
      Header: <span id="imagestore-header-name">Name</span>,
      accessor: 'name',
      className: 'imagestore-name',
    },
    {
      Header: <span id="imagestore-header-default">Default Store</span>,
      accessor: 'default',
      className: 'imagestore-default',
    },
    {
      Header: <span id="imagestore-header-basePath">Base Path</span>,
      accessor: 'basePath',
      className: 'imagestore-basepath',
    },
    {
      Header: <span id="imagestore-header-layour">Layout</span>,
      accessor: 'layout',
      className: 'imagestore-layout',
    },
    {
      Header: <span id="imagestore-header-codec">Codec</span>,
      accessor: 'codec',
      className: 'imagestore-codec',
    },
    {
      Header: <span id="imagestore-header-diskSize">Disk Size (GB)</span>,
      accessor: 'diskSize',
      className: 'imagestore-disksize',
    },
    {
      Header: <span id="imagestore-header-availableSize">Available Size (GB)</span>,
      accessor: 'availableSize',
      className: 'imagestore-availablesize',
    },
  ];

  const getDiskSizeText = (size: number): string => {
    switch (size) {
      case undefined:
      case null:
        return '';
      case -1:
        return 'The system cannot find the path specified.';
      case 0:
        return 'N/A';
      default:
        return size.toString();
    }
  };

  const setupTable = useCallback(() => {
    codecList.map((codec) => {});
    const tableData = imageStores.map((temp, index) => {
      const entry = {
        selector: (
          <RMCheckBoxY isChecked={selectedRowIndex === index} onChange={() => {}} />
        ),
        name: (
          <TextInput
            type="text"
            value={temp.name}
            id={`imagestores-setuptable-name-${index}`}
            rowData={temp}
            onChange={(e) => onchangeName(e.value, index)}
            index={index}
          />
        ),
        default: (
          <input
            id={`imagestores-setuptable-default-${index}`}
            type="radio"
            checked={temp.isDefault}
            onChange={() => handleDefaultChange(index)}
            className="w-4 h-4"
          />
        ),
        basePath: (
          <TextInput
            type="text"
            value={temp.basePath}
            id={`imagestores-setuptable-basepath-${index}`}
            rowData={temp}
            onChange={(e) => onChangeBasePath(e.value, e.rowData.basePath, index)}
            index={index}
          />
        ),
        layout: (
          <TextInput
            type="text"
            value={temp.layout}
            id={`imagestores-setuptable-layout-${index}`}
            rowData={temp}
            onChange={(e) => onChangeLayout(e.value, index)}
            index={index}
            disabled={true}
          />
        ),
        codec: (
          <EISelectBox
            labelText=""
            selectedValue={temp.codec}
            onChange={(e) => onChangeCodec(e.target.value, index)}
            options={codecList}
            selectStyle={{ width: '100%' }}
            containerStyle={{
              width: '100%',
              display: 'felx',
              justifyContent: 'space-between',
            }}
            disabled={false}
          />
        ),
        diskSize: (
          <TextInput
            type="text"
            value={getDiskSizeText(temp.diskSize)}
            title={getDiskSizeText(temp.diskSize)}
            id={`imagestores-setuptable-disksize-${index}`}
            rowData={temp}
            onChange={() => {}}
            index={index}
            disabled={true}
          />
        ),
        availableSize: (
          <TextInput
            type="text"
            value={getDiskSizeText(temp.availableSize)}
            title={getDiskSizeText(temp.availableSize)}
            id={`imagestores-setuptable-availablesize-${index}`}
            rowData={temp}
            onChange={() => {}}
            index={index}
            disabled={true}
          />
        ),
      };
      return entry;
    });
    setValues(tableData);
  }, [imageStores, selectedRowIndex]);

  return (
    <>
      <div id='imagestores-wrapper' className="border-box mt-2 ml-1 float-left p-0 image-store-container h-[26%]">
        <div id="imagestores-header-label-wrapper" className="float-left">
          <label id="imagestores-header-label" className="row m-2 font-weight-bold">Image Stores</label>
        </div>
        <div id="imagestores-action-buttons" className="float-right pr-3">
          <div
            id="imagestores-button-refresh"
            className="p-1 float-left cursor-pointer"
            onClick={(e) => fetchImageScores()}
          >
            <RMSvgIcon
              id="imagestores-icon-refresh"
              name="Refresh"
              width={'28px'}
              height={'28px'}
              fill="#474747"
              subfolder="Setup"
            ></RMSvgIcon>
          </div>
          <div id="imagestores-button-add" className="p-1 float-left cursor-pointer" onClick={(e) => addNewConfig()}>
            <RMSvgIcon
              id="imagestores-icon-add"
              name="Add"
              width={'28px'}
              height={'28px'}
              fill="#474747"
              subfolder="Setup"
            ></RMSvgIcon>
          </div>

          <div id="imagestores-button-remove" className="p-1 float-left cursor-pointer" onClick={(e) => deleteConfig()}>
            <RMSvgIcon
              id="imagestores-icon-remove"
              name="Remove"
              width={'28px'}
              height={'28px'}
              opacity={selectedRowIndex === -1 ? 0.5 : 1}
              fill="#474747"
              subfolder="Setup"
            ></RMSvgIcon>
          </div>
        </div>
        <div id="imagestores-table-wrapper" className="image-store-table-parent">
          <div id="imagestores-table" className="image-store-table">{setupTempTable()}</div>
        </div>
        <RMModal
          id="imagestores-modal"
          showModal={modalDetails.showModal}
          setShowModal={modalDetails.setShowModal}
          body={modalDetails.body}
          heading={modalDetails.heading}
          showButtons={modalDetails.showButtons}
          onOk={() => modalDetails.onOk()}
          onCancel={() => modalDetails.onCancel()}
          isOkOnly={modalDetails.isOkOnly}
          okButtonText={modalDetails.okButtonText}
        />
      </div>
    </>
  );
});
